import React from 'react'
import { ErrorMessage } from './ErrorMessage'
import { LoadingMessage } from './LoadingMessage'

export interface StepContentProps {
    /** When true, shows the loading state instead of children */
    isLoading: boolean
    /** When true and not loading, shows the error state instead of children */
    isError: boolean
    /** Message shown in the loading state (e.g. "Loading ads from Meta...") */
    loadingMessage: string
    /** Main error message (e.g. "Failed to load Meta ads.") */
    errorMessage: string
    /** Optional error instance; when present and detail is not set, detail uses error.message */
    error?: unknown
    /** Optional explicit detail text for the error message */
    errorDetail?: string
    /** Rendered when not loading and not error */
    children: React.ReactNode
}

/**
 * Shared layout for step content: centered loading or error state,
 * or children when ready. Uses the same flex centering so steps look consistent.
 */
export function StepContent({
    isLoading,
    isError,
    loadingMessage,
    errorMessage,
    error,
    errorDetail,
    children,
}: StepContentProps) {
    if (isLoading) {
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center">
                <LoadingMessage message={loadingMessage} />
            </div>
        )
    }
    if (isError) {
        const detail =
            errorDetail ??
            (error instanceof Error
                ? `${error.message} Please try again.`
                : 'Please try again.')
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center">
                <ErrorMessage message={errorMessage} detail={detail} />
            </div>
        )
    }
    return <>{children}</>
}
